import { InputHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { FiEdit2, FiEye, FiEyeOff } from "react-icons/fi";
import { ButtonWithIcon } from "newui/components/Button";
import FormikFieldError from "../FormikFieldError";
import { getInputClasses, getInputWrapperClasses } from "./inputStyles";
import LabelWithGuideline from "../LabelWithGuideline";

export interface BaseTextInputWithLabelProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
	className?: string;
	customIcon?: React.ReactNode;
	disableToggler?: boolean;
	errorText?: string;
	fixedPlaceholder?: boolean;
	flexGrow?: number;
	guideLine?: string;
	hasError?: boolean;
	icon?: boolean;
	iconPosition?: "left" | "right";
	inputClassName?: string;
	/**
	 * Ref to the underlying `<input>`. Exposes focus(), so it can be handed to a
	 * modal's `initialFocusRef` to land focus on a real control instead of
	 * whatever decorative element happens to be first in the tab order (usually
	 * the close button). Mirrors `SelectWithLabel.selectRef`.
	 */
	inputRef?: React.Ref<HTMLInputElement>;
	label?: string;
	noMargin?: boolean;
	placeholder?: string;
	placeholderSize?: string;
	required?: boolean;
	testId?: string;
	value?: string | number;
	formatValue?: (value: string) => string;
	handleToggler?: (value: boolean) => void;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const TextInputWithLabel: React.FC<BaseTextInputWithLabelProps> = ({
	label,
	placeholderSize,
	required,
	placeholder,
	className = "",
	inputClassName = "",
	icon,
	inputRef,
	customIcon,
	formatValue,
	disableToggler,
	errorText,
	hasError = false,
	guideLine,
	onChange,
	iconPosition,
	testId,
	flexGrow,
	noMargin,
	fixedPlaceholder,
	handleToggler,
	value,
	disabled,
	id,
	name,
	type = "text",
	...restProps
}) => {
	const [visible, setVisible] = useState(false);
	const [pHolder, setPHolder] = useState(placeholder);
	const inputElement = useRef<HTMLInputElement | null>(null);
	const [toggleButton, setToggleButton] = useState(
		disableToggler || disabled ? true : false
	);

	useEffect(() => setToggleButton(disabled), [disabled]);

	// The internal ref drives formatValue/the visibility toggler, so a caller's
	// `inputRef` has to be merged in rather than replace it.
	const assignInputRef = useCallback(
		(node: HTMLInputElement | null) => {
			inputElement.current = node;

			if (typeof inputRef === "function") {
				inputRef(node);
				return;
			}

			if (inputRef) {
				// React types RefObject.current as readonly; populating a caller's
				// ref object is exactly what React itself does internally here.
				(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
			}
		},
		[inputRef]
	);

	const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!formatValue) {
			return;
		}

		let val = (e.target as HTMLInputElement).value;
		const key = e.keyCode || (e as any).charCode;

		if (key === 8) {
			return;
		}

		val = formatValue(val);
		if (formatValue && inputElement.current) {
			let val = e.currentTarget.value;
			const key = e.keyCode || (e as any).charCode;

			if (key === 8) {
				return;
			}

			val = formatValue(val);
			inputElement.current.value = val;
		}
	};

	const onToggleButton = () => {
		if (handleToggler) {
			handleToggler(!toggleButton);
		}

		setToggleButton(!toggleButton);
		setPHolder("");
		inputElement.current?.focus();
	}

	const hasInputError = !!errorText || hasError;

	return (
		<div
			className={getInputWrapperClasses(hasInputError, noMargin, className)}
			style={{ flexGrow: flexGrow || 0 }}
		>
			<LabelWithGuideline label={label} htmlFor={id || name} guideLine={guideLine} required={required} />

			{disableToggler && toggleButton && (
				<div className="absolute right-8 top-9">
					<ButtonWithIcon
						icon={<FiEdit2 />} // Pencil
						text="CHANGE"
						btntype="secondary"
						width="8rem"
						onClick={onToggleButton}
					/>
				</div>
			)}

			{icon && !toggleButton && (
				<>
					{customIcon ? (
						<div className="absolute left-5 top-11 cursor-pointer">
							{customIcon}
						</div>
					) : (
						<div className='absolute cursor-pointer right-8 top-11'>
							{!visible ? (
								<FiEye onClick={() => setVisible(true)} size={20} /> // OpenEye
							) : (
								<FiEyeOff onClick={() => setVisible(false)} size={20} /> // CloseEye
							)}
						</div>
					)}
				</>
			)}

			<input
				ref={assignInputRef}
				onKeyUp={onKeyUp}
				placeholder={fixedPlaceholder ? placeholder : pHolder}
				onChange={onChange}
				type={icon && !visible ? "password" : visible && type === 'password' ? 'text' : type}
				className={getInputClasses(hasInputError, `h-14 max-h-14 ${inputClassName}`)}
				style={{
					paddingLeft: iconPosition === "left" ? "50px" : "16px",
					paddingRight: "16px",
					fontSize: placeholderSize || undefined,
				}}
				value={value}
				disabled={disabled}
				id={id}
				name={name}
				data-testid={testId}
				{...restProps}
			/>

			<FormikFieldError text={errorText} />
		</div>
	);
};